import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { supabase, saveSpeedrunRecord } from '../supabaseClient';

const SpeedrunTips = ({ userId }) => {
  const [selectedGame, setSelectedGame] = useState('');
  const [speedruns, setSpeedruns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userRecords, setUserRecords] = useState([]);
  const [uploadingRecord, setUploadingRecord] = useState(false);
  const [newRecord, setNewRecord] = useState({
    time: '',
    videoUrl: ''
  });

  useEffect(() => {
    loadUserRecords();
  }, [userId]);

  const loadUserRecords = async () => {
    try {
      const { data, error } = await supabase
        .from('speedruns')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUserRecords(data || []);
    } catch (error) {
      console.error('Error loading user records:', error);
    }
  };

  const searchSpeedruns = async () => {
    if (!selectedGame) {
      alert('Please enter a game name');
      return;
    }

    setLoading(true);
    try {
      // First get the game ID
      const gameResponse = await axios.get(
        `https://www.speedrun.com/api/v1/games?name=${encodeURIComponent(selectedGame)}`,
        { headers: { 'User-Agent': 'XKID-Gaming-Chatbot' } }
      );

      if (gameResponse.data.data.length > 0) {
        const gameId = gameResponse.data.data[0].id;
        
        // Then get the records for that game
        const recordsResponse = await axios.get(
          `https://www.speedrun.com/api/v1/runs?game=${gameId}&status=verified&orderby=date&direction=desc&max=20`,
          { headers: { 'User-Agent': 'XKID-Gaming-Chatbot' } }
        );

        setSpeedruns(recordsResponse.data.data);
      } else {
        alert('Game not found on speedrun.com');
      }
    } catch (error) {
      console.error('Error fetching speedruns:', error);
      alert('Error fetching speedruns. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitRecord = async () => {
    if (!selectedGame || !newRecord.time || !newRecord.videoUrl) {
      alert('Please fill in all fields');
      return;
    }

    setUploadingRecord(true);
    try {
      await saveSpeedrunRecord(userId, {
        game: selectedGame,
        time: newRecord.time,
        videoUrl: newRecord.videoUrl
      });
      
      setNewRecord({ time: '', videoUrl: '' });
      await loadUserRecords();
      alert('Record submitted for verification!');
    } catch (error) {
      console.error('Error submitting record:', error);
      alert('Error submitting record. Please try again.');
    } finally {
      setUploadingRecord(false);
    }
  };

  const formatTime = (timeString) => {
    // Convert PT1H2M3S format to readable time
    const matches = timeString.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!matches) return timeString;
    
    const hours = matches[1] ? `${matches[1]}h ` : '';
    const minutes = matches[2] ? `${matches[2]}m ` : '';
    const seconds = matches[3] ? `${matches[3]}s` : '';
    
    return `${hours}${minutes}${seconds}`.trim();
  };

  return (
    <div className="space-y-6 p-4 bg-xkid-gray rounded-lg border border-xkid-green">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-xkid-green mb-4">Speedrun Records</h2>
        <p className="text-gray-400">View and submit speedrun records!</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xkid-green mb-2">Game</label>
          <input
            type="text"
            value={selectedGame}
            onChange={(e) => setSelectedGame(e.target.value)}
            placeholder="Enter game name..."
            className="w-full p-3 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
          />
        </div>

        <button
          onClick={searchSpeedruns}
          disabled={loading}
          className="w-full p-3 rounded-lg bg-xkid-green text-black font-bold hover:bg-xkid-green-dark transition-colors disabled:opacity-50"
        >
          {loading ? 'Searching...' : 'Search Records'}
        </button>

        <div className="mt-8">
          <h3 className="text-xl font-bold text-xkid-green mb-4">Submit Your Record</h3>
          <div className="space-y-4">
            <input
              type="text"
              value={newRecord.time}
              onChange={(e) => setNewRecord(prev => ({ ...prev, time: e.target.value }))}
              placeholder="Time (e.g., 1:23.456)"
              className="w-full p-3 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
            />
            <input
              type="url"
              value={newRecord.videoUrl}
              onChange={(e) => setNewRecord(prev => ({ ...prev, videoUrl: e.target.value }))}
              placeholder="Video proof URL"
              className="w-full p-3 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
            />
            <button
              onClick={submitRecord}
              disabled={uploadingRecord}
              className="w-full p-3 rounded-lg bg-xkid-red text-white font-bold hover:bg-xkid-red-dark transition-colors disabled:opacity-50"
            >
              {uploadingRecord ? 'Submitting...' : 'Submit Record'}
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4 mt-8">
        <h3 className="text-xl font-bold text-xkid-green">Top Records</h3>
        {speedruns.map((run) => (
          <div
            key={run.id}
            className="p-4 rounded-lg border border-xkid-green bg-xkid-gray-light"
          >
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-bold text-xkid-green">{run.players[0]?.name || 'Anonymous'}</h4>
                <p className="text-gray-300 mt-2">Time: {formatTime(run.times.primary)}</p>
                <p className="text-gray-300">Date: {new Date(run.date).toLocaleDateString()}</p>
              </div>
              {run.videos?.links?.[0]?.uri && (
                <a
                  href={run.videos.links[0].uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1 rounded bg-xkid-green text-black text-sm hover:bg-xkid-green-dark transition-colors"
                >
                  Watch
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-4 mt-8">
        <h3 className="text-xl font-bold text-xkid-green">Your Records</h3>
        {userRecords.map((record) => (
          <div
            key={record.id}
            className="p-4 rounded-lg border border-xkid-green bg-xkid-gray-light"
          >
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-bold text-xkid-green">{record.game}</h4>
                <p className="text-gray-300 mt-2">Time: {record.time}</p>
                <p className="text-gray-300">
                  Status: {record.verified ? 'Verified' : 'Pending Verification'}
                </p>
                <p className="text-gray-300">
                  Submitted: {new Date(record.created_at).toLocaleDateString()}
                </p>
              </div>
              {record.video_proof && (
                <a
                  href={record.video_proof}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1 rounded bg-xkid-green text-black text-sm hover:bg-xkid-green-dark transition-colors"
                >
                  Watch
                </a>
              )}
            </div>
          </div>
        ))}
        {userRecords.length === 0 && (
          <p className="text-center text-gray-400">No records submitted yet. Be the first!</p>
        )}
      </div>
    </div>
  );
};

export default SpeedrunTips; 